import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";

const TODAY = new Date().toISOString().slice(0, 10);
const PLUS = (days) => {
  const d = new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const empty = {
  tenant_id: "", lot_id: "", lease_type: "Short Term",
  check_in: TODAY, check_out: PLUS(7),
  rent_amount: 385, popm_fee: 0, discount: 0, deposit: 100,
  source: "Direct", notes: "",
};
const emptyGroup = {
  tenant_id: "", lot_ids: [], lease_type: "Group",
  check_in: TODAY, check_out: PLUS(7),
  rent_amount_per_lot: 385, popm_fee_per_lot: 0, discount: 0, deposit: 200,
  source: "Direct", notes: "",
};

export default function ReservationsPage() {
  const { currentPark } = usePark();
  const [reservations, setReservations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [lots, setLots] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [gform, setGform] = useState(emptyGroup);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [r, t, l] = await Promise.all([
        api.get(`/reservations${filter ? `?status=${filter}` : ""}`),
        api.get("/tenants"),
        api.get("/lots"),
      ]);
      setReservations(r.data); setTenants(t.data); setLots(l.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (currentPark?.id) load(); }, [filter, currentPark?.id]);

  // Available lots filter when dialog dates change
  useEffect(() => {
    if (!open || !form.check_in || !form.check_out) return;
    api.get(`/lots/available?check_in=${form.check_in}&check_out=${form.check_out}`)
      .then(({ data }) => setAvailableLots(data))
      .catch(() => setAvailableLots([]));
  }, [open, form.check_in, form.check_out]);

  useEffect(() => {
    if (!groupOpen || !gform.check_in || !gform.check_out) return;
    api.get(`/lots/available?check_in=${gform.check_in}&check_out=${gform.check_out}`)
      .then(({ data }) => setAvailableLots(data))
      .catch(() => setAvailableLots([]));
  }, [groupOpen, gform.check_in, gform.check_out]);

  const tenantLabel = (id) => {
    const t = tenants.find((x) => x.id === id);
    return t ? `${t.first_name} ${t.last_name}` : "—";
  };
  const lotLabel = (id) => {
    const l = lots.find((x) => x.id === id);
    return l ? `#${l.number}` : "—";
  };

  const create = async () => {
    if (!form.tenant_id || !form.lot_id) { toast.error("Tenant and lot are required"); return; }
    try {
      const { data } = await api.post("/reservations", form);
      if (data?._overlap_warning) {
        const w = data._overlap_warning;
        toast.warning(
          `Reservation created — but overlaps existing reservation ${w.confirmation_no} `
          + `(${w.check_in} → ${w.check_out}, ${w.status}). `
          + `Check-in will be blocked until that one is checked out.`,
          { duration: 8000 },
        );
      } else {
        toast.success("Reservation created");
      }
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createGroup = async () => {
    if (!gform.tenant_id || gform.lot_ids.length < 2) {
      toast.error("Select a tenant and at least 2 lots");
      return;
    }
    try {
      const { data } = await api.post("/reservations/group", gform);
      toast.success(`Group ${data.group_no}: ${data.count} reservations created`);
      setGroupOpen(false);
      setGform(emptyGroup);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleLot = (id) => {
    setGform((g) => ({
      ...g,
      lot_ids: g.lot_ids.includes(id) ? g.lot_ids.filter((x) => x !== id) : [...g.lot_ids, id],
    }));
  };

  return (
    <div data-testid="reservations-page">
      <PageHeader
        title="Reservations"
        subtitle={`${reservations.length} record${reservations.length === 1 ? "" : "s"}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => setGroupOpen(true)} data-testid="new-group-reservation-button">
              <Users size={14} className="mr-2" /> Group booking
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="new-reservation-button">
              <Plus size={14} className="mr-2" /> New Reservation
            </Button>
          </div>
        }
      />

      {/* Single reservation dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!bg-white max-w-lg border-[#EAEAE5]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>New Reservation</DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">Only lots without conflicts for the chosen dates are shown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Tenant</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                <SelectTrigger data-testid="res-tenant-select"><SelectValue placeholder="Select tenant…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#737A76]">Check in</Label>
                <Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="res-checkin-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#737A76]">Check out</Label>
                <Input type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="res-checkout-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Lot ({availableLots.length} available)</Label>
              <Select value={form.lot_id} onValueChange={(v) => setForm({ ...form, lot_id: v })}>
                <SelectTrigger data-testid="res-lot-select"><SelectValue placeholder={availableLots.length ? "Select lot…" : "No lots available"} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableLots.map((l) => <SelectItem key={l.id} value={l.id}>#{l.number} · {l.size} · {formatCurrency(l.monthly_rent)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Lease type</Label>
              <Select value={form.lease_type} onValueChange={(v) => setForm({ ...form, lease_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Short Term">Short Term</SelectItem>
                  <SelectItem value="Long Term">Long Term</SelectItem>
                  <SelectItem value="Sub Rental">Sub Rental</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Rent</Label><Input type="number" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">POPM Fee</Label><Input type="number" value={form.popm_fee} onChange={(e) => setForm({ ...form, popm_fee: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Discount</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Deposit</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={create} data-testid="create-reservation-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group reservation dialog */}
      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="!bg-white max-w-2xl border-[#EAEAE5]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Group Booking</DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">Reserve multiple lots for one tenant — perfect for rallies and family gatherings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Tenant (group leader)</Label>
              <Select value={gform.tenant_id} onValueChange={(v) => setGform({ ...gform, tenant_id: v })}>
                <SelectTrigger data-testid="group-tenant-select"><SelectValue placeholder="Select tenant…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Check in</Label><Input type="date" value={gform.check_in} onChange={(e) => setGform({ ...gform, check_in: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Check out</Label><Input type="date" value={gform.check_out} onChange={(e) => setGform({ ...gform, check_out: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Rent per lot</Label><Input type="number" value={gform.rent_amount_per_lot} onChange={(e) => setGform({ ...gform, rent_amount_per_lot: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">POPM per lot</Label><Input type="number" value={gform.popm_fee_per_lot} onChange={(e) => setGform({ ...gform, popm_fee_per_lot: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Total deposit</Label><Input type="number" value={gform.deposit} onChange={(e) => setGform({ ...gform, deposit: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Pick lots ({gform.lot_ids.length} selected · {availableLots.length} available)</Label>
              <div className="mt-2 grid grid-cols-6 gap-2 max-h-60 overflow-y-auto p-3 border border-[#EAEAE5] rounded-lg bg-[#FAFAF7]" data-testid="group-lot-picker">
                {availableLots.length === 0 ? (
                  <div className="col-span-6 text-sm text-[#737A76] text-center py-6">No lots available for the selected dates.</div>
                ) : availableLots.map((l) => {
                  const selected = gform.lot_ids.includes(l.id);
                  return (
                    <button
                      type="button"
                      key={l.id}
                      onClick={() => toggleLot(l.id)}
                      data-testid={`group-lot-${l.number}`}
                      className={`p-2 rounded-md text-xs border transition ${selected ? "bg-[#284B3B] text-white border-[#284B3B]" : "bg-white border-[#EAEAE5] hover:border-[#284B3B]"}`}
                    >
                      <div className="font-medium">#{l.number}</div>
                      <div className="opacity-70">{l.size}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupOpen(false)}>Cancel</Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={createGroup} data-testid="create-group-submit">Create {gform.lot_ids.length || ""} reservation{gform.lot_ids.length === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 mb-4">
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-white border-[#EAEAE5]" data-testid="reservation-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Reservation">Reservation</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Closed">Closed</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm pd-table">
            <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
              <th className="px-4 py-3">Conf. #</th><th>Group</th><th>Tenant</th><th>Lot</th><th>Type</th><th>Check In</th><th>Check Out</th><th>Days</th><th>Total</th><th>Status</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="px-4 py-8 text-center text-[#737A76]">Loading…</td></tr>
              : reservations.length === 0 ? <tr><td colSpan={10} className="px-4 py-8 text-center text-[#737A76]">No reservations.</td></tr>
              : reservations.map((r) => (
                <tr key={r.id} className="border-b border-[#F0F0EC] last:border-0 cursor-pointer" onClick={() => navigate(`/reservations/${r.id}`)} data-testid={`reservation-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <span>{r.confirmation_no}</span>
                      {r._conflicts?.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#FBE9C8] text-[#8A5A00] border border-[#F0D49A]"
                          title={`Overlaps with: ${r._conflicts.map((c) => `${c.confirmation_no} (${c.status}, ${c.check_in} → ${c.check_out})`).join("; ")}. Check-in will be blocked while a prior reservation is Active.`}
                          data-testid={`reservation-conflict-badge-${r.id}`}
                        >
                          ⚠ Overlap
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-[#737A76]">{r.group_no || "—"}</td>
                  <td>{tenantLabel(r.tenant_id)}</td>
                  <td>{lotLabel(r.lot_id)}</td>
                  <td>{r.lease_type}</td>
                  <td>{formatDate(r.check_in)}</td>
                  <td>{formatDate(r.check_out)}</td>
                  <td>{r.days}</td>
                  <td>{formatCurrency(r.total)}</td>
                  <td><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
